import { isNativeAppRuntime } from '@/biz/tool';
import { getAhaBridge } from '@/app/bridge/ahaBridge';
import { isNonProductionEnv } from '@/utils/getEnv';

type VConsoleInstance = {
  destroy?: () => void;
  hide?: () => void;
  hideSwitch?: () => void;
  show?: () => void;
  showSwitch?: () => void;
};

let loadingVConsole: Promise<VConsoleInstance | null> | null = null;

const getVConsoleInstance = () =>
  (window as Window & { __ahaVConsole?: VConsoleInstance }).__ahaVConsole;

const setVConsoleInstance = (instance: VConsoleInstance) => {
  (window as Window & { __ahaVConsole?: VConsoleInstance }).__ahaVConsole =
    instance;
};

const clearVConsoleInstance = () => {
  delete (window as Window & { __ahaVConsole?: VConsoleInstance })
    .__ahaVConsole;
};

export const toggleNativeVConsole = async () => {
  if (typeof window === 'undefined' || !isNonProductionEnv()) return false;

  const existing = getVConsoleInstance();
  if (existing) {
    existing.destroy?.();
    existing.hideSwitch?.();
    existing.hide?.();
    clearVConsoleInstance();
    loadingVConsole = null;
    return true;
  }

  const shouldPrintNativeInfo = !loadingVConsole;
  if (!loadingVConsole) {
    loadingVConsole = import(/* webpackChunkName: "vconsole" */ 'vconsole')
      .then((mod) => {
        const VConsole = mod.default;
        const instance = new VConsole();
        setVConsoleInstance(instance);
        return instance;
      })
      .catch((error) => {
        loadingVConsole = null;
        console.error('[AhaDebug] failed to open vConsole', error);
        return null;
      });
  }

  const instance = await loadingVConsole;
  if (instance && shouldPrintNativeInfo) {
    try {
      console.info(
        '[AhaBridge] native info',
        isNativeAppRuntime() ? await getAhaBridge().runtime.getInfo() : null
      );
    } catch (error) {
      console.error('[AhaBridge] failed to print native info', error);
    }
  }

  return Boolean(instance);
};
